import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

/**
 * Sticky Detection Modifier
 *
 * Usage:
 * <element {{sticky-detector}}>
 *
## Parameters

| Parameter | Type | Default | Description |
|-----------|------|---------|-------------|
| `threshold` | Number | `1` | Intersection Observer threshold (0-1) |
| `rootMargin` | String | `'0px'` | Intersection Observer root margin |
| `onStuck` | Function | `null` | Callback when element becomes stuck |
| `onUnstuck` | Function | `null` | Callback when element becomes unstuck |
 *
 * The modifier will add/remove the 'is-stuck' class based on the sticky state.
 * It uses a simple approach to detect when the sticky element is stuck.
 */
export default class StickyDetectorModifier extends Modifier {
  element = null;
  isStuck = false;
  onStuck = null;
  onUnstuck = null;
  _scrollHandler = null;
  _rafId = null;
  _initialTop = null;

  constructor() {
    super(...arguments);
    this._scrollHandler = this.handleScroll.bind(this);
    registerDestructor(this, () => this.cleanup());
  }

  modify(element, positionalArgs = [], namedArgs = {}) {
    this.element = element;
    this.onStuck = namedArgs.onStuck;
    this.onUnstuck = namedArgs.onUnstuck;

    // Start observing
    this.startObserving();
  }

  startObserving() {
    if (!this.element) return;

    // Store the initial position
    this._initialTop = this.element.getBoundingClientRect().top;

    // Add scroll listener
    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Check initial state
    this.checkStickyState();
  }

  handleScroll() {
    // Use requestAnimationFrame to throttle the scroll handler
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }

    this._rafId = requestAnimationFrame(() => {
      this.checkStickyState();
    });
  }

  checkStickyState() {
    if (!this.element || this._initialTop === null) return;

    const rect = this.element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(this.element);
    const position = computedStyle.position;

    // Only check if element has sticky positioning
    if (position !== 'sticky') {
      return;
    }

    // Get the sticky offset
    const top = parseInt(computedStyle.top, 10) || 0;

    // Element is stuck when its current position is different from its natural position
    // and it's at the sticky offset position
    const isStuck = Math.abs(rect.top - top) < 2 && rect.top !== this._initialTop;

    // Update state if changed
    if (isStuck !== this.isStuck) {
      this.isStuck = isStuck;
      this.updateStuckState();
    }
  }

  updateStuckState() {
    if (!this.element) return;

    if (this.isStuck) {
      this.element.classList.add('is-stuck');
      if (this.onStuck && typeof this.onStuck === 'function') {
        this.onStuck(this.element);
      }
    } else {
      this.element.classList.remove('is-stuck');
      if (this.onUnstuck && typeof this.onUnstuck === 'function') {
        this.onUnstuck(this.element);
      }
    }
  }

  cleanup() {
    // Remove scroll listener
    window.removeEventListener('scroll', this._scrollHandler);

    // Cancel any pending animation frame
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }

    // Remove the class when cleaning up
    if (this.element) {
      this.element.classList.remove('is-stuck');
    }
  }
}
